import React, { useEffect, useMemo } from 'react';
import { Box, Tabs, Tab, Typography, Card, CardContent, Chip, Stack } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import NfseFiscalConfigPage from './NfseFiscalConfigPage';
import SupportConfigPage from './SupportConfigPage';
import BankIntegrationConfigPage from './BankIntegrationConfigPage';
import GeneralSystemConfigPage from './GeneralSystemConfigPage';
import MongoBackupConfigPage from './MongoBackupConfigPage';
import { VideosPage } from './VideosPage';
import { UsersPage } from './UsersPage';
import { useAuth } from '../contexts/AuthContext';

type ConfigTabKey =
  | 'nfse'
  | 'bank'
  | 'support'
  | 'general'
  | 'landing-videos'
  | 'admin-users'
  | 'mongo-backup';

function normalizeTab(raw: string | null): ConfigTabKey {
  const v = (raw || '').trim().toLowerCase();
  if (!v) return 'general';
  if (v === 'nfse' || v === 'fiscal' || v === 'nfse-fiscal') return 'nfse';
  if (
    v === 'bank' ||
    v === 'integracao-bancaria' ||
    v === 'integração-bancária' ||
    v === 'bank-integration' ||
    v === 'beneficiary' ||
    v === 'beneficiario' ||
    v === 'beneficiário' ||
    v === 'receitas' ||
    v === 'inter' ||
    v === 'banco-inter' ||
    v === 'webhooks' ||
    v === 'webhook' ||
    v === 'inter-webhooks' ||
    v === 'interwebhooks'
  ) {
    return 'bank';
  }
  if (v === 'support' || v === 'suporte') return 'support';
  if (v === 'general' || v === 'geral' || v === 'sistema') return 'general';
  if (v === 'landing-videos' || v === 'videos-landingpage' || v === 'vídeos-landingpage') {
    return 'landing-videos';
  }
  if (v === 'admin-users' || v === 'usuarios' || v === 'usuários') return 'admin-users';
  if (v === 'mongo-backup' || v === 'backup' || v === 'backup-mongo') return 'mongo-backup';
  return 'general';
}

function TabPanel(props: { value: ConfigTabKey; tab: ConfigTabKey; children: React.ReactNode }) {
  const { value, tab, children } = props;
  if (value !== tab) return null;
  return <Box sx={{ pt: 2 }}>{children}</Box>;
}

const ConfigurationsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { admin } = useAuth();
  const isSuperAdmin = admin?.role === 'super_admin';

  const tab = useMemo(() => normalizeTab(searchParams.get('tab')), [searchParams]);

  /** Migra URLs antigas (?tab=beneficiary|webhooks) para ?tab=bank&bankSection=… */
  useEffect(() => {
    const v = (searchParams.get('tab') || '').toLowerCase();
    const legacyBeneficiary =
      v === 'beneficiary' ||
      v === 'beneficiario' ||
      v === 'beneficiário' ||
      v === 'receitas' ||
      v === 'inter' ||
      v === 'banco-inter';
    const legacyWebhooks =
      v === 'webhooks' || v === 'webhook' || v === 'inter-webhooks' || v === 'interwebhooks';
    if (!legacyBeneficiary && !legacyWebhooks) return;
    setSearchParams(
      prev => {
        const p = new URLSearchParams(prev);
        p.set('tab', 'bank');
        p.set('bankSection', legacyWebhooks ? 'webhooks' : 'beneficiary');
        return p;
      },
      { replace: true }
    );
  }, [searchParams, setSearchParams]);

  const handleChange = (_: React.SyntheticEvent, next: ConfigTabKey) => {
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      p.set('tab', next);
      if (next === 'bank' && !p.get('bankSection')) {
        p.set('bankSection', 'beneficiary');
      }
      return p;
    });
  };

  return (
    <Box>
      <Card variant="outlined" sx={{ mb: 2, bgcolor: 'action.hover' }}>
        <CardContent>
          <Stack spacing={1.5}>
            <Typography variant="h5">Configurações</Typography>
            <Typography variant="body2" color="text.secondary">
              Central de parâmetros operacionais do control plane. Separe mudanças globais, fiscais,
              bancárias e de comunicação para reduzir risco e facilitar auditoria.
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip size="small" color="primary" label="Escopo global" />
              <Chip
                size="small"
                variant="outlined"
                label="Algumas alterações afetam cobrança/NFS-e"
              />
              <Chip size="small" variant="outlined" label="Teste integrações antes de produção" />
            </Stack>
          </Stack>
        </CardContent>
      </Card>
      <Card variant="outlined">
        <Tabs
          value={tab}
          onChange={handleChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ px: 2, pt: 1, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Geral (sistema)" value="general" />
          <Tab label="Fiscal NFSe" value="nfse" />
          <Tab label="Integração bancária" value="bank" />
          <Tab label="Suporte" value="support" />
          <Tab label="Vídeos Landing Page" value="landing-videos" />
          <Tab label="Usuários" value="admin-users" />
          {isSuperAdmin && <Tab label="Backup Mongo" value="mongo-backup" />}
        </Tabs>
      </Card>

      <TabPanel value={tab} tab="general">
        <GeneralSystemConfigPage />
      </TabPanel>
      <TabPanel value={tab} tab="nfse">
        <NfseFiscalConfigPage />
      </TabPanel>
      <TabPanel value={tab} tab="bank">
        <BankIntegrationConfigPage />
      </TabPanel>
      <TabPanel value={tab} tab="support">
        <SupportConfigPage />
      </TabPanel>
      <TabPanel value={tab} tab="landing-videos">
        <VideosPage
          allowedFolders={['public']}
          title="Vídeos Landing Page"
          description="Vídeos públicos usados na landing page e áreas institucionais. Materiais privados de produto ficam em Produtos > Vídeos."
        />
      </TabPanel>
      <TabPanel value={tab} tab="admin-users">
        <UsersPage defaultTab="admin" showTenantUsersTab={false} showAdminUsersTab />
      </TabPanel>
      {isSuperAdmin && (
        <TabPanel value={tab} tab="mongo-backup">
          <MongoBackupConfigPage />
        </TabPanel>
      )}
    </Box>
  );
};

export default ConfigurationsPage;
